<template>
  <FriedModal @close="handleClose">
    <template #header>
      <FriedModalHeader :header="$t('invite-recipient')" />
    </template>
    <div v-if="!forwarded" class="invite-modal">
      <FriedParagraph class="invite-description">
        {{ $t('please-enter-recipient-details') }}
      </FriedParagraph>
      <div v-if="allowRoleTransfer" class="transfer">
        <FriedSwitch
          data-external="signing-page-invite-transfer-signature-toggle"
          class="global-result-toggle"
          :model-value="selectedTransferOption"
          :label="$t('transfer-signature-role-to-this-person?')"
          @update:modelValue="selectedTransferOption = !selectedTransferOption"
        />
      </div>
      <FriedInput
        v-model.trim="email"
        data-external="invite-modal-email-input"
        class="full-length-input"
        :label="$t('email') + '*'"
        :required="true"
        :error-message="emailError"
        @update:modelValue="updateInput(InviteInputType.Email)"
      />
      <div class="invite-recipient-two-fields">
        <FriedInput
          v-model.trim="firstName"
          data-external="invite-modal-first-name-input"
          :label="$t('first-name') + '*'"
          :required="true"
          :error-message="firstNameError"
          @update:modelValue="updateInput(InviteInputType.FirstName)"
        />
        <FriedInput
          v-model.trim="lastName"
          data-external="invite-modal-last-name-input"
          :label="$t('last-name') + '*'"
          :required="true"
          :error-message="lastNameError"
          @update:modelValue="updateInput(InviteInputType.LastName)"
        />
      </div>
      <div class="invite-recipient-two-fields">
        <FriedInput
          v-model.trim="companyName"
          data-external="invite-modal-company-name-input"
          :label="$t('company-name')"
          :required="true"
          @update:modelValue="updateInput(InviteInputType.CompanyName)"
        />
        <FriedInput
          v-model.trim="companyNumber"
          data-external="invite-modal-company-number-input"
          :label="$t('business-registration-number')"
          :required="true"
          @update:modelValue="updateInput(InviteInputType.CompanyNumber)"
        />
      </div>
      <div class="invite-recipient-two-fields">
        <FriedInput
          v-model.trim="title"
          data-external="invite-modal-title-input"
          :label="$t('title')"
          @update:modelValue="updateInput(InviteInputType.Title)"
        />
        <FriedInput
          v-model.trim="mobile"
          data-external="invite-modal-telephone-input"
          class="full-length-input"
          :label="$t('mobile-number') + ' (+46701234567)'"
          :required="hasSmsToSign"
          :error-message="mobileError"
          @update:modelValue="updateInput(InviteInputType.Mobile)"
        />
      </div>
    </div>
    <div v-else>
      <FriedH4>{{ $t('document-has-been-forwarded') }}</FriedH4>
      <div class="recipient-notified">{{ $t('the-recipient-has-been-notified') }}</div>
    </div>
    <template #footer>
      <FriedModalButtonsFooter>
        <template v-if="!forwarded">
          <FriedButton
            data-external="invite-cancel-button"
            :button-type="ButtonType.Secondary"
            @click="handleClose"
          >
            {{ $t('cancel') }}
          </FriedButton>
          <div
            v-tooltip="{
              disabled: canInvite,
              content: $t('cr_max_recipients_reached_tooltip'),
            }"
          >
            <FriedButton
              data-external="invite-modal-invite-btn"
              class="invite-button"
              :loading="loading"
              :disabled="!canInvite"
              @click="handleInvite"
            >
              {{ $t('invite') }}
            </FriedButton>
          </div>
        </template>
        <FriedButton
          v-else
          data-external="invite-modal-close-btn"
          :button-type="ButtonType.Secondary"
          @click="handleClose"
        >
          {{ $t('close') }}
        </FriedButton>
      </FriedModalButtonsFooter>
    </template>
  </FriedModal>
</template>

<script lang="ts">
import { ButtonType } from '@getaccept/fried-tofu';
import { RecipientRole } from '@getaccept/lib-shared-new/src/recipients/enums/recipient-role';
import { sanitizeEmail } from '@getaccept/lib-shared-new/src/helpers';
import type { SigningPageRecipient } from '@getaccept/lib-shared-new/src/types/signing-page-recipient';
import { ModalSizes } from '@getaccept/lib-shared-new/src/enums/ModalSizes';
import type { PropType } from 'vue';
import { computed, ref, defineComponent } from 'vue';
import { InviteInputType } from '../../types/enums/invite-input-type';
import type { InviteRecipientInfo } from '../../types/invite-recipient-info';
import { InviteModalHelper } from '../../helpers/invite-modal.helper';

export default defineComponent({
  props: {
    forwarded: {
      type: Boolean,
      default: false,
    },
    hasSmsToSign: {
      type: Boolean,
      default: false,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    recipient: {
      type: Object as PropType<SigningPageRecipient>,
      required: true,
    },
    canInvite: {
      type: Boolean,
      required: true,
    },
    isSigned: {
      type: Boolean,
      default: false,
    },
    isSigningForward: {
      type: Boolean,
      default: false,
    },
  },
  setup(props, { emit }) {
    const selectedTransferOption = ref(false);
    const firstName = ref('');
    const lastName = ref('');
    const email = ref('');
    const mobile = ref('');
    const title = ref('');
    const companyName = ref('');
    const companyNumber = ref('');
    const firstNameError = ref('');
    const lastNameError = ref('');
    const emailError = ref('');
    const mobileError = ref('');

    const isMissingRequiredInput = computed(
      () =>
        !!firstNameError.value || !!lastNameError.value || !!emailError.value || !!mobileError.value
    );
    const allowRoleTransfer = computed(
      () =>
        props.recipient.role === RecipientRole.Signer && !props.isSigned && props.isSigningForward
    );
    const cleanEmail = computed((): string => sanitizeEmail(email.value));

    const updateInput = (inputType: InviteInputType) => {
      if (!isMissingRequiredInput.value) {
        return;
      }
      switch (inputType) {
        case InviteInputType.FirstName:
          firstNameError.value = '';
          break;
        case InviteInputType.LastName:
          lastNameError.value = '';
          break;
        case InviteInputType.Email:
          emailError.value = '';
          break;
        case InviteInputType.Mobile:
          mobileError.value = '';
          break;
      }
    };

    const handleInvite = () => {
      setErrorMessages();
      if (isMissingRequiredInput.value) {
        return;
      }
      const recipientInfo: InviteRecipientInfo = {
        firstName: firstName.value,
        lastName: lastName.value,
        email: cleanEmail.value,
        mobile: mobile.value,
        title: title.value,
        companyName: companyName.value,
        companyNumber: companyNumber.value,
        transferSignature: !!selectedTransferOption.value,
      };
      emit('invite', recipientInfo);
    };

    const setErrorMessages = () => {
      firstNameError.value = InviteModalHelper.validateName('first', firstName.value);
      lastNameError.value = InviteModalHelper.validateName('last', lastName.value);
      emailError.value = InviteModalHelper.validateEmail(cleanEmail.value);
      mobileError.value = InviteModalHelper.validateMobile(mobile.value, props.hasSmsToSign);
    };

    const handleClose = () => {
      emit('close');
    };

    return {
      ModalSizes,
      ButtonType,
      InviteInputType,
      selectedTransferOption,
      firstName,
      lastName,
      email,
      mobile,
      title,
      companyName,
      companyNumber,
      firstNameError,
      lastNameError,
      emailError,
      mobileError,
      isMissingRequiredInput,
      allowRoleTransfer,
      cleanEmail,
      updateInput,
      handleInvite,
      setErrorMessages,
      handleClose,
    };
  },
});
</script>
<style lang="scss" scoped>
.invite-modal {
  .full-length-input {
    width: 100%;
    padding: var(--spacing-50) 0;
  }

  .half-length-input {
    width: 50%;
    padding: var(--spacing-50) 0;
  }

  .invite-description {
    margin-top: 0;
    padding-bottom: var(--spacing-50);
  }

  .required-mark {
    color: var(--error-dark);
  }

  .invite-recipient-two-fields {
    display: flex;
    justify-content: space-between;
    margin: var(--spacing-50) 0;

    > * {
      width: 49%;
    }

    @media only screen and (max-width: $xs) {
      display: block;

      > * {
        width: 100%;
      }
    }
  }

  .transfer {
    margin: var(--spacing-50) 0;
    display: flex;
    flex-direction: column;

    .transfer-description {
      margin-bottom: var(--spacing-50);
    }
  }

  :deep(.input-container) {
    .input-label {
      text-transform: inherit;
      color: var(--text-gray);
    }
  }
}

.invite-button {
  width: 100%;
}
</style>
